import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

interface QuickGuideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface GuideSection {
  category: string;
  items: { id: string; label: string }[];
}

const guideSections: GuideSection[] = [
  {
    category: "Add Content",
    items: [
      { id: "upload", label: "Upload, paste, or record" },
      { id: "website", label: "Website links" },
      { id: "record", label: "Record Lectures & Meetings" },
    ],
  },
  {
    category: "Chat",
    items: [
      { id: "chat-ai", label: "Chat with AI" },
      { id: "mention", label: "Mention Tools in Chat" },
      { id: "ask-content", label: "Ask about any content" },
    ],
  },
  {
    category: "Summary, Chapters, Transcripts",
    items: [
      { id: "summaries", label: "Generate summaries, chapters, and transcripts" },
      { id: "prompts", label: "Summary prompts" },
    ],
  },
  {
    category: "Flashcards",
    items: [{ id: "practice", label: "Practice flashcards" }],
  },
  {
    category: "Quizzes",
    items: [
      { id: "quiz-style", label: "Choose your quiz style" },
      { id: "study-quiz", label: "Study with quizzes" },
    ],
  },
  {
    category: "Voice Mode",
    items: [{ id: "voice", label: "Learn with voice mode" }],
  },
  {
    category: "Exams",
    items: [{ id: "exams", label: "Test your knowledge on everything" }],
  },
  {
    category: "Rooms & Room Chat",
    items: [
      { id: "spaces", label: "Rooms & room chat" },
      { id: "share", label: "Share your room" },
    ],
  },
  {
    category: "Other",
    items: [{ id: "languages", label: "Languages, AI Models, & More" }],
  },
];

// Flatten all items for navigation
const allItems = guideSections.flatMap((section) => section.items);

export const QuickGuideDialog = ({ open, onOpenChange }: QuickGuideDialogProps) => {
  const { isRtl } = useTranslation();
  const [selectedItem, setSelectedItem] = useState("upload");
  const [activeTab, setActiveTab] = useState<"upload" | "paste" | "record">("upload");
  // Reset dialog state when closed
  useEffect(() => {
    if (!open) {
      setSelectedItem("upload");
      setActiveTab("upload");
    }
  }, [open]);

  // Get current index and total count
  const currentIndex = allItems.findIndex((item) => item.id === selectedItem);
  const totalItems = allItems.length;

  const handleNext = () => {
    const nextIndex = (currentIndex + 1) % totalItems;
    setSelectedItem(allItems[nextIndex].id);
  };

  const handleBack = () => {
    const prevIndex = currentIndex === 0 ? totalItems - 1 : currentIndex - 1;
    setSelectedItem(allItems[prevIndex].id);
  };

  const getContentForItem = (itemId: string) => {
    switch (itemId) {
      case "upload":
        return {
          title: "Upload, paste, or record",
          description:
            "Upload, paste, or record content to start learning. To add content in one click, install the Shattara AI Chrome Extension.",
          videoUrl:
            activeTab === "upload"
              ? "/assets/shattaraai_upload_feature.mp4"
              : activeTab === "paste"
                ? "/assets/shattaraai_paste_feature.mp4"
                : "/assets/shattaraai_record_feature.mp4",
        };
      case "chat-ai":
        return {
          title: "Chat with AI",
          description: "Chat with AI to get instant answers and explanations from your content.",
          videoUrl: "/assets/shattaraai_chatwithai_feature.mp4",
        };
      case "mention":
        return {
          title: "Mention Tools in Chat",
          description: "Use @ mentions to bring powerful tools directly into your chat conversation.",
          videoUrl: "/assets/shattaraai_mentiontoolsinchat_feature.mp4",
        };
      case "ask-content":
        return {
          title: "Ask about any content",
          description: "Ask questions about any content you've uploaded and get accurate, sourced answers.",
          videoUrl: "/assets/shattaraai_askaboutanycontent_feature.mp4",
        };
      case "website":
        return {
          title: "Website links",
          description: "You can now paste website URLs to quickly understand the content.",
          videoUrl: "/assets/shattaraai_websitelinks_feature.mp4",
        };
      case "study-quiz":
        return {
          title: "Study with quizzes",
          description:
            "Study your topic with quizzes that break down into concepts, a chat to ask questions, feedback, and sources.",
          videoUrl: "/assets/shattaraai_studywithquizzes_feature.mp4",
        };
      case "languages":
        return {
          title: "Languages, AI Models, & More",
          description: "Choose from 20+ languages and 4+ AI models in account settings.",
          videoUrl: "/assets/shattaraai_languages_aimodels_more_feature.mp4",
        };
      case "record":
        return {
          title: "Record Lectures & Meetings",
          description: "Record Google Meet or browser tabs and get notes from your sessions instantly.",
          videoUrl: "/assets/shattaraai_record_feature.mp4",
        };
      case "summaries":
        return {
          title: "Generate summaries, chapters, and transcripts",
          description: "Get brief summaries, chapter breakdowns, and transcripts of your content.",
          videoUrl: "/assets/shattaraai_summary_intro_1.mp4",
        };
      case "prompts":
        return {
          title: "Summary prompts",
          description: "Get the exact information you need by creating custom prompts for your summaries.",
          videoUrl: "/assets/shattaraai_summary_prompts.mp4",
        };
      case "practice":
        return {
          title: "Practice with Flashcards",
          description: "Learn with Shattara AI that simplifies ideas and guides you to the right sources.",
          videoUrl: "/assets/shattaraai_flashcards_feature.mp4",
        };
      case "quiz-style":
        return {
          title: "Choose your quiz style",
          description: "Create quizzes with MCQs, free response, fill in the blank, T/F, and set difficulty levels from easy to hard.",
          videoUrl: "/assets/shattaraai_choosequizstyle_feature.mp4",
        };
      case "exams":
        return {
          title: "Test your knowledge on everything",
          description: "Create a room with all your contents to build mock exams, get answer breakdowns, track your progress, and review summary.",
          videoUrl: "/assets/shattaraai_learnwithexams_feature.mp4",
        };
      case "spaces":
        return {
          title: "Rooms & room chat",
          description: "Add contents to your rooms, chat with all your contents, and much more!",
          videoUrl: "/assets/shattaraai_roomsandroomchat_feature.mp4",
        };
      case "share":
        return {
          title: "Share your room",
          description: "Make your rooms public, and share your room link with teachers, classmates, or anyone!",
          videoUrl: "/assets/shattaraai_shareyourroom_feature.mp4",
        };
      case "voice":
        return {
          title: "Learn with voice mode",
          description: "Learn with Shattara AI that simplifies ideas and guides you to the right sources. P.S. Say 'Please' to Shattara AI.",
          videoUrl: "/assets/shattaraai_voicemode_feature.mp4",
        };
      default:
        return {
          title: "Feature Preview",
          description: "This feature helps you learn more effectively.",
          videoUrl: "https://dj2sofb25vegx.cloudfront.net/signup_modal/upload_youtube_video.mp4",
        };
    }
  };

  const content = getContentForItem(selectedItem);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[425px] md:max-w-[600px] lg:max-w-6xl lg:w-[85vw] lg:max-h-[85vh] lg:h-[85vh] lg:p-0 lg:overflow-hidden lg:flex p-6"
        dir={isRtl ? "rtl" : "ltr"}
      >
        <div className="lg:flex lg:h-full lg:w-full">
          {/* Sidebar - Hidden on mobile */}
          <div className="hidden lg:flex w-64 bg-muted/30 border-r border-border flex-col overflow-hidden">
            <div className="p-6 border-b border-border flex-shrink-0">
              <DialogTitle className="text-2xl font-normal">Quick Guide</DialogTitle>
              <DialogDescription className="text-base mt-1">Explore 8+ features</DialogDescription>
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
              <div className="p-4">
                <div className="space-y-4 pb-4">
                  {guideSections.map((section) => (
                    <div key={section.category}>
                      <h3 className="text-xs font-semibold text-primary mb-2 px-2">{section.category}</h3>
                      <div className="space-y-1">
                        {section.items.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => setSelectedItem(item.id)}
                            className={`w-full text-left p-2 rounded-lg transition-colors hover:bg-primary/5 hover:text-primary ${
                              selectedItem === item.id
                                ? "bg-primary/5 text-primary"
                                : "bg-background/50 text-primary/80"
                            }`}
                          >
                            <div className="font-normal text-sm line-clamp-1">{item.label}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:flex-1 lg:flex lg:flex-col">
            <div className="flex flex-col items-center lg:p-6">
              <h2 className="tracking-tight text-xl lg:text-2xl lg:mb-2 w-full text-left lg:hidden">{content.title}</h2>
              <h2 className="hidden lg:block tracking-tight text-2xl mb-2 w-full text-left">{content.title}</h2>
              <p className="text-muted-foreground lg:text-base w-full text-left">{content.description}</p>

              <div className="w-full mt-4">
                {selectedItem === "upload" && (
                  <div className="flex space-x-2 mb-4">
                    <Button
                      variant={activeTab === "upload" ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => setActiveTab("upload")}
                    >
                      Upload
                    </Button>
                    <Button
                      variant={activeTab === "paste" ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => setActiveTab("paste")}
                    >
                      Paste
                    </Button>
                    <Button
                      variant={activeTab === "record" ? "default" : "outline"}
                      className="flex-1"
                      onClick={() => setActiveTab("record")}
                    >
                      Record
                    </Button>
                  </div>
                )}

                <div className="mt-2 relative w-full h-[220px] sm:h-[240px] md:h-[340px] lg:h-[500px] xl:h-[600px] border-2 border-primary/20 rounded-lg bg-muted">
                  <video
                    className="w-full h-full rounded-md object-fill"
                    src={content.videoUrl}
                    poster="/video-placeholder.png"
                    autoPlay
                    playsInline
                    preload="auto"
                    crossOrigin="anonymous"
                    loop
                    muted
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Footer */}
        <div className="lg:hidden">
          <div className="flex justify-between w-full items-center sm:justify-between flex-row pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              {currentIndex + 1}/{totalItems}
            </div>
            <div className="flex gap-2">
              {currentIndex > 0 && (
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
              )}
              {currentIndex < totalItems - 1 ? (
                <Button onClick={handleNext}>Next</Button>
              ) : (
                <Button onClick={() => onOpenChange(false)}>Close</Button>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Footer */}
        <div className="hidden lg:block absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-background">
          <div className="flex justify-between items-center">
            <div className="text-sm text-muted-foreground">
              {currentIndex + 1}/{totalItems}
            </div>
            <div className="flex gap-2">
              {currentIndex > 0 && (
                <Button variant="outline" onClick={handleBack}>
                  Back
                </Button>
              )}
              {currentIndex < totalItems - 1 ? (
                <Button onClick={handleNext}>Next</Button>
              ) : (
                <Button onClick={() => onOpenChange(false)}>Close</Button>
              )}
            </div>
          </div>
        </div>

        {/* Close Button */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-lg opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">Close</span>
        </button>
      </DialogContent>
    </Dialog>
  );
};
